import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { labelService } from '../services/label';
import { 
  LabelData, 
  LabelSettingField, 
  SaveLabelSettingsRequest 
} from '../types/label';

// Mapping between API f_control or field_title and LabelData property names
// Mapping between API f_control or field_title and LabelData property names
const MAPPING: Record<string, keyof LabelData> = {
  // Branding
  'showLogo': 'showLogo',
  'logoText': 'logoText',
  'showIgQR': 'instagramQR',
  'igQRUrl': 'instagramUrl',
  'showParcelpicksBranding': 'showParcelpicksBranding',
  'showYourBranding': 'showYourBranding',

  // Delivery
  'custNumber': 'customerNumber',
  'custName': 'customerName',
  'custEmail': 'customerEmail',
  'paymentMode': 'paymentMode',
  'payment_mode': 'paymentMode',
  'paymentType': 'paymentMode',
  'payMode': 'paymentMode',
  'custAddress': 'customerAddress',
  'routingCode': 'routingCode',
  'showDimensions': 'showDimensions',
  'showWeight': 'showWeight',

  // Pickup
  'pickupNumber': 'pickupNumber',
  'pickupContact': 'pickupNumber',
  'pickupName': 'pickupName',
  'pickupEmail': 'pickupEmail',
  'pickupPaymentMode': 'paymentMode',
  'pickupAddress': 'pickupAddress',

  // Order
  'product': 'product',
  'sku': 'sku',
  'qty': 'qty',
  'amount': 'amount',
  'showOrderNumber': 'showOrderNumber',
  
  'note': 'note',
};

const DEFAULT_LABEL_FIELDS: LabelSettingField[] = []; // We'll handle defaults in the component or fetch

export function useLabel() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalData, setOriginalData] = useState<any>(null);

  const fetchSettings = useCallback(async (userId: number | string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await labelService.getLabelSettings(userId);
      
      let fields: LabelSettingField[] = [];
      let fullResponseData: any = res;

      // Flatten fields from tabOne and tabTwo
      if (res) {
        if (Array.isArray((res as any).tabOne)) {
          (res as any).tabOne.forEach((sec: any) => {
            if (sec.fieldList) fields = fields.concat(sec.fieldList);
          });
        }
        if (Array.isArray((res as any).tabTwo)) {
          (res as any).tabTwo.forEach((sec: any) => {
            if (sec.fieldList) fields = fields.concat(sec.fieldList);
          });
        }

        // Fallback for flat array responses just in case
        if (fields.length === 0) {
          if (Array.isArray(res)) {
            fields = res;
          } else if (res && Array.isArray((res as any).data)) {
            fields = (res as any).data;
          } else if (res && Array.isArray((res as any).fieldList)) {
            fields = (res as any).fieldList;
          } else if (res && (res as any).data && Array.isArray((res as any).data.fieldList)) {
            fields = (res as any).data.fieldList;
          }
        }
      }

      if (fields.length > 0) {
        setOriginalData(fullResponseData);
        
        const mappedData: Partial<LabelData> = {};
        fields.forEach((field: LabelSettingField) => {
          const key = MAPPING[field.f_control] || MAPPING[field.field_title];
          if (key) {
            if (typeof field.is_checked === 'number') {
              // @ts-ignore
              mappedData[key] = field.is_checked === 1;
            }
            
            if (field.f_values) {
              if (key === 'logoText' || key === 'instagramUrl' || key === 'note') {
                // @ts-ignore
                mappedData[key] = field.f_values;
              }
              if (field.f_control === 'showLogo') {
                mappedData.logoPreview = field.f_values;
              }
            }
            
            if (field.f_control === 'showLogo' && field.f_innerField) {
               try {
                  const inner = JSON.parse(field.f_innerField);
                  if (inner.imageUrl) {
                      mappedData.logoPreview = inner.imageUrl;
                  }
               } catch(e) {}
            }

            if (field.f_control === 'showYourBranding' && field.f_innerField) {
               try {
                  const inner = JSON.parse(field.f_innerField);
                  if (inner.logoText) mappedData.logoText = inner.logoText;
                  else if (inner.text) mappedData.logoText = inner.text;
                  else if (inner.f_values) mappedData.logoText = inner.f_values;
               } catch(e) {}
            }

            if (field.f_control === 'showIgQR') {
                if (field.f_values) {
                    mappedData.instagramUrl = field.f_values;
                }
                if (field.f_innerField) {
                   try {
                      const inner = JSON.parse(field.f_innerField);
                      if (inner.igQRUrl) mappedData.instagramUrl = inner.igQRUrl;
                      else if (inner.instagramUrl) mappedData.instagramUrl = inner.instagramUrl;
                      else if (inner.f_values) mappedData.instagramUrl = inner.f_values;
                   } catch(e) {}
                }
            }
          }

          if (field.f_control === 'paperSize' && field.is_checked === 1) {
            if (field.field_title.includes('A4')) {
              mappedData.labelSize = 'a4';
            } else if (field.field_title.includes('A6')) {
              mappedData.labelSize = 'a6';
            }
          }
        });
        return mappedData;
      }
    } catch (err: any) {

      setError(err?.message || 'Failed to fetch label settings');
      toast.error('Error fetching label settings');
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  const saveSettings = useCallback(async (userId: number | string, currentData: LabelData, updatedOriginalData?: any) => {
    try {
      setLoading(true);
      setError(null);

      const dataToProcess = updatedOriginalData || originalData;

      if (!dataToProcess) {
         toast.error('No settings to save');
         return false;
      }

      // Deep clone to avoid mutating state directly
      const payload: any = JSON.parse(JSON.stringify(dataToProcess));

      const updateField = (field: LabelSettingField) => {
        const key = MAPPING[field.f_control] || MAPPING[field.field_title];
        let isChecked = field.is_checked;
        let value = field.f_values;

        if (key) {
          // @ts-ignore
          isChecked = currentData[key] ? 1 : 0;
          
          if (key === 'logoText' || key === 'instagramUrl' || key === 'note') {
            // @ts-ignore
            value = currentData[key];
          }
          if (field.f_control === 'showLogo') {
            value = currentData.logoPreview;
          }
        }

        if (field.f_control === 'showYourBranding') {
           try {
               const inner = field.f_innerField ? JSON.parse(field.f_innerField) : {};
               inner.logoText = currentData.logoText;
               inner.f_values = currentData.logoText;
               field.f_innerField = JSON.stringify(inner);
           } catch(e) {}
           value = currentData.logoText;
        }

        if (field.f_control === 'showIgQR') {
           try {
               const inner = field.f_innerField ? JSON.parse(field.f_innerField) : {};
               inner.igQRUrl = currentData.instagramUrl;
               inner.instagramUrl = currentData.instagramUrl;
               inner.f_values = currentData.instagramUrl;
               field.f_innerField = JSON.stringify(inner);
           } catch(e) {}
           value = currentData.instagramUrl;
        }

        if (field.f_control === 'paperSize') {
          if (field.field_title.includes('A4')) {
            isChecked = currentData.labelSize === 'a4' ? 1 : 0;
          } else if (field.field_title.includes('A6')) {
            isChecked = currentData.labelSize === 'a6' ? 1 : 0;
          }
        }

        field.is_checked = isChecked;
        field.f_values = value;
      };

      if (payload.tabOne && Array.isArray(payload.tabOne)) {
         payload.tabOne.forEach((sec: any) => {
             if (sec.fieldList) sec.fieldList.forEach(updateField);
         });
      }
      
      if (payload.tabTwo && Array.isArray(payload.tabTwo)) {
         payload.tabTwo.forEach((sec: any) => {
             if (sec.fieldList) sec.fieldList.forEach(updateField);
         });
      }

      // Flatten the nested structure into a flat list for the backend
      const flatFieldList: LabelSettingField[] = [];
      if (payload.tabOne) {
        payload.tabOne.forEach((sec: any) => {
          if (sec.fieldList) flatFieldList.push(...sec.fieldList);
        });
      }
      if (payload.tabTwo) {
        payload.tabTwo.forEach((sec: any) => {
          if (sec.fieldList) flatFieldList.push(...sec.fieldList);
        });
      }

      const finalPayload = {
        user_id: Number(userId),
        fieldList: flatFieldList
      };

      const res = await labelService.saveLabelSettings(finalPayload);
      
      // The API seems to return { data: "Data Save Successfully" } instead of { success: true }
      const isSuccess = res.success === true || 
                       (res as any).data === "Data Save Successfully" || 
                       (res as any).status === "success" ||
                       (typeof res === 'string' && res === "Data Save Successfully");

      if (isSuccess) {
        toast.success('Label settings saved successfully');
        setOriginalData(payload);
        return true;
      } else {
        toast.error(res.message || (res as any).data || 'Failed to save settings');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to save label settings');
      toast.error('Error saving label settings');
    } finally {
      setLoading(false);
    }
    return false;
  }, [originalData]);

  const uploadLogo = useCallback(async (file: File, userId: number | string) => {
    try {
      setLoading(true);
      const res = await labelService.uploadLogo(file, userId);
      // API might return success: true or just the data
      const logoUrl = res.url || (res as any).uploadUrl;
      
      if (res.success || logoUrl) {
        toast.success('Logo uploaded successfully');
        return logoUrl;
      } else {
        toast.error(res.message || 'Failed to upload logo');
      }
    } catch (err: any) {
      toast.error('Error uploading logo');
    } finally {
      setLoading(false);
    }
    return null;
  }, []);



  return {
    loading,
    error,
    fetchSettings,
    saveSettings,
    uploadLogo,
  };
}
